
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export function EmailTester() {
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recipient || !subject || !content) {
      toast({
        variant: "destructive",
        title: "Missing fields",
        description: "Please fill out all fields",
      });
      return;
    }
    
    setLoading(true);
    setErrorDetails(null);
    
    try {
      console.log("Sending test email to:", recipient);
      
      const { data, error } = await supabase.functions.invoke('mailer', {
        body: {
          to: recipient,
          subject: subject,
          html: content,
        }
      });
      
      if (error) {
        console.error('Email sending error:', error);
        setErrorDetails(error.message || "Failed to send email");
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to send email",
        });
        return;
      }
      
      console.log('Response from mailer function:', data);
      
      if (!data.success) {
        console.error('Email sending failed:', data.error);
        setErrorDetails(data.error || "Failed to send email");
        toast({
          variant: "destructive",
          title: "Error",
          description: data.error || "Failed to send email",
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Email sent successfully",
      });
      
      console.log('Email sent:', data);
      
    } catch (error: any) {
      console.error('Error calling mailer function:', error);
      setErrorDetails(error.message || "Failed to send email");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send email",
      });
    } finally {
      setLoading(false);
    }
  };

  // Use a default value when accessing environment variables
  const emailProvider = import.meta.env.VITE_EMAIL_PROVIDER || 'console (default)';

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Email Tester</CardTitle>
        <CardDescription>
          Test the email functionality by sending a test email
        </CardDescription>
      </CardHeader>
      <CardContent>
        {errorDetails && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error sending email</AlertTitle>
            <AlertDescription>
              {errorDetails}
            </AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="recipient">Recipient Email</Label>
            <Input
              id="recipient"
              placeholder="Enter recipient email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              placeholder="Enter email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="content">HTML Content</Label>
            <Textarea
              id="content"
              placeholder="Enter HTML content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              required
            />
          </div>
          
          <div className="p-3 bg-muted rounded-md text-sm">
            <h4 className="font-medium mb-2">Email Environment Configuration</h4>
            <p><strong>Current Provider:</strong> {emailProvider}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              To switch between providers, set the VITE_EMAIL_PROVIDER environment variable in Supabase.
              For Gmail, also set VITE_GMAIL_USER and VITE_GMAIL_APP_PASSWORD.
            </p>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-between flex-wrap gap-4">
        <div className="text-sm text-muted-foreground">
          In development mode, emails are logged to console
        </div>
        <Button type="submit" onClick={handleSubmit} disabled={loading}>
          {loading ? "Sending..." : "Send Email"}
        </Button>
      </CardFooter>
    </Card>
  );
}
